import { ArrowDownRight, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import Image from "next/image";
import { Button, buttonVariants } from "@/components/atoms/buttons";
import { __ } from "@/lib/i18n/functions";
import { Locales } from "@/lib/i18n";
import { LinkField } from "@prismicio/client";
import { LinkHelper } from "@/components/atoms/link-helper";

type CreditProps = {
  variant?: "headingDown" | "default";
  state: "default" | "open";
  name?: string;
  image?: string;
  description?: string;
  button?: {
    text: string;
    link?: string | LinkField;
  };
  className?: string;
  showRightArrow?: boolean;
  locale: Locales;
  tag?: string;
  link?: string | LinkField;
};

const Credit = ({
  variant = "default",
  state,
  name,
  image,
  description,
  button,
  className,
  locale,
  tag = __("Photographed by", locale),
  showRightArrow = true,
  link,
}: CreditProps) => {
  const isOpen = state === "open";
  const isDefault = variant === "default";

  return (
    <article
      className={cn(
        "bg-white-primary shadow-festi flex w-full gap-3 px-3.5 py-2.5",
        variant === "headingDown" ? "flex-col-reverse" : "flex-col",
        isOpen ? "rounded-2xl" : "rounded-full",
        className
      )}
    >
      <LinkHelper link={link} className="absolute inset-0">
        <span className="sr-only">{tag || name}</span>
      </LinkHelper>

      <div className="flex w-full items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {tag && <span className="text-sm text-gray-700">{tag}</span>}{" "}
          {image && (
            <figure className="relative h-6.5 w-6.5 overflow-hidden rounded-full">
              <Image src={image} alt={name || ""} fill className="absolute object-cover" />
            </figure>
          )}
          {name && <figcaption className="text-sm font-medium">{name}</figcaption>}{" "}
        </div>

        {isDefault && showRightArrow && (
          <>
            <hr className="h-5 w-px bg-gray-300" />
            <ArrowDownRight
              className="shrink-0 transition-all duration-200 ease-in-out group-hover/bento:-rotate-90"
              aria-hidden="true"
              size={20}
            />
          </>
        )}
      </div>

      {isOpen && (
        <div className="space-y-3">
          {description && (
            <blockquote
              cite="#"
              className={cn(
                "max-w-[17rem] text-xs",
                variant === "headingDown" && "font-cabinet-grotesk text-sm leading-tight font-medium md:text-[17px]"
              )}
            >
              {description}
            </blockquote>
          )}
          {isDefault &&
            button?.text &&
            (button.link ? (
              <LinkHelper
                link={button.link}
                className={cn(buttonVariants({ variant: "rounded", size: "md" }), "w-full text-sm font-medium")}
              >
                {button.text}
                <ArrowRight size={20} aria-hidden="true" />
              </LinkHelper>
            ) : (
              <Button
                aria-label={`Perform action: ${button.text}`}
                variant="rounded"
                size="md"
                className="w-full text-sm font-medium"
              >
                {button.text}
                <ArrowRight size={20} aria-hidden="true" />
              </Button>
            ))}
        </div>
      )}
    </article>
  );
};

export default Credit;
